import React, { useCallback } from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import clsx from 'clsx'
import { useQueryClient } from 'react-query'

import { Text } from '@wartek-id/text'
import { Shimmer } from '@wartek-id/shimmer'
import { Banner, BannerDescription } from '@wartek-id/banner'
import { Icon } from '@wartek-id/icon'
import { analyticsEventHandler } from '@wartek-id/toolbox-analytics-event-handler'

import YoutubePlayer from 'app/PelatihanMandiriPage/components/YoutubePlayer'
import {
  useGetMateriVideo,
  usePostMateriProgress,
} from 'api/microlearning/useAPIMateri'
import { extractYTIdFromUrl, sanitize, toKebabCase } from 'utils/text'
import { GATrackEvent } from 'utils/googleAnalytics'
import { useSETrackerEvent } from 'app/PelatihanMandiriPage/hooks/useSETrackEvent'
import useMateriRouterCheck from 'app/PelatihanMandiriPage/hooks/useMateriRouterCheck'
import QueryNotCompleteError from 'app/PelatihanMandiriPage/components/QueryNotCompleteError'
import { MILKvKeys, useGetKeyPairValue } from 'api/microlearning/useAPIKeyValue'
import { useGetDeviceId } from 'api/microlearning/useAPIDeviceId'
import KMDisclaimer from '../components/KMDisclaimer'
import { invalidateLearningProgress } from '../utils/detailNavigation'

const MateriVideo = (props) => {
  const { isDesktop } = props
  const { query, isReady: isRouterReady, back } = useRouter()
  const queryClient = useQueryClient()
  const getDeviceId = useGetDeviceId()

  const {
    modul = query.modul as string,
    materi = query.materi as string,
    id = query.id as string,
    topik = query.topik as string,
    topik_name = query.topik_name as string,
  } = props

  const [hasPlayed, setHasPlayed] = React.useState(false)
  const [isPlaying, setIsPlaying] = React.useState(false)
  const isRouterInvalid = useMateriRouterCheck(isRouterReady, query)
  const {
    isSuccess,
    isLoading,
    data: materiContent,
  } = useGetMateriVideo(modul as string, materi as string, id as string)

  const { recordProgress = 'true' } = query
  const shouldRecordProgress = recordProgress === 'true'

  const disabledVideoByTopic = useGetKeyPairValue(MILKvKeys.DisabledMateriVideo)
  const { enabled: analyticsEventHandlerEnabled, sendMoEngageEvent } =
    analyticsEventHandler()
  const { SETrackMoEngageEvent } = useSETrackerEvent()

  const { mutateAsync: saveProgress } = usePostMateriProgress(
    modul as string,
    materi as string,
    {
      onSuccess: async () => {
        await invalidateLearningProgress(
          queryClient,
          modul as string,
          topik as string
        )
      },
    }
  )

  const videoPlayedCallback = useCallback((target) => {
    if (target.data === 1) {
      setIsPlaying(true)
      setTimeout(() => {
        setHasPlayed(true)
      }, 100)
    } else if (target.data === 2 || target.data === 3) {
      setIsPlaying(false)
    }
  }, [])

  const handleMoEngageTrackPlayVideo = useCallback(() => {
    if (analyticsEventHandlerEnabled) {
      sendMoEngageEvent('watch_videos_pm')
    }
    SETrackMoEngageEvent({
      activityType: 'WATCH_VIDEOS_PM',
      deviceId: getDeviceId.data?.deviceId,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsEventHandlerEnabled, getDeviceId.data?.deviceId])

  React.useEffect(() => {
    if (!hasPlayed && isPlaying) {
      shouldRecordProgress &&
        saveProgress({
          id: materiContent?.data?.id,
          type: 'video',
        })
      GATrackEvent({
        eventName: 'learning_activity',
        eventParams: {
          event_trigger: 'module_video_click_play',
          action_type: 'play_material_video',
          meta: JSON.stringify({
            learning_id: materiContent?.data?.id,
            material_id: materi,
            module_id: modul,
          }),
        },
        sendDefaultParams: true,
      })
      handleMoEngageTrackPlayVideo()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, materiContent, hasPlayed, isPlaying, saveProgress, materi, modul])

  if (isRouterInvalid) {
    return <QueryNotCompleteError isDesktop={isDesktop} />
  }

  if (isLoading) {
    return (
      <div className="p-4">
        <Shimmer
          height={12}
          noOfLines={9}
          className="mb-4 last:mb-0"
          style={{ width: '100%' }}
        />
      </div>
    )
  }

  return (
    isSuccess && (
      <>
        <Head>
          <title>
            Pelatihan Mandiri | Materi {materiContent?.data?.name ?? ' Video'}
          </title>
        </Head>
        {isDesktop && (
          <div className="flex flex-row px-6 pt-6">
            {query.from && (
              <Icon
                as="i"
                className="mr-2"
                data-testid="back-button"
                onClick={back}
              >
                arrow_back
              </Icon>
            )}
            <div>
              <Text className="pb-1">MATERI</Text>
              <Text className="pb-1" variant="heading-lg">
                {materiContent?.data?.name ?? ' Video'}
              </Text>
            </div>
          </div>
        )}
        <div
          data-testid="materi-video-container"
          className={clsx(
            'relative pb-[18px] bg-white',
            isDesktop && 'mt-2 p-2'
          )}
          style={{
            minHeight: 'calc(100vh - 10rem)',
          }}
        >
          {disabledVideoByTopic.isSuccess &&
          Array.isArray(disabledVideoByTopic.data.data) &&
          (disabledVideoByTopic.data.data.includes(-1) ||
            disabledVideoByTopic.data.data.includes(
              parseInt(topik as string)
            )) ? (
            <div className="px-4 pt-4">
              <Banner
                className="mb-4"
                animationDuration={300}
                onOpenStateChange={function noRefCheck() {}}
                title="Video tidak tersedia"
                type="warning"
                data-testid="error-banner"
                closable={false}
              >
                <BannerDescription>
                  Mohon maaf atas gangguan pada layanan kami, materi video untuk
                  topik <strong>{topik_name}</strong> saat ini tidak dapat
                  diakses. Kami sedang mencoba untuk mengatasi masalah ini pada
                  sistem. Mohon menunggu dan silakan mencoba lagi nanti.
                  <br />
                  <br />
                  Terima kasih.
                </BannerDescription>
              </Banner>
            </div>
          ) : (
            <div className="w-full" data-testid="yt-player-container">
              <YoutubePlayer
                isVerticalVideo={materiContent.data?.isVertical}
                videoId={extractYTIdFromUrl(materiContent.data?.url)}
                onStateChange={videoPlayedCallback}
              />
            </div>
          )}
          <div className="p-4">
            <div className="mb-4 leading-relaxed">
              <Text
                data-title={toKebabCase(materiContent.data?.name)}
                variant="heading-sm"
              >
                {materiContent.data?.name}
              </Text>
            </div>
            <Text
              as="div"
              className="simple-wysiwyg-content material-content"
              dangerouslySetInnerHTML={{
                __html: sanitize(materiContent.data.description),
              }}
            />
            <KMDisclaimer className="mt-6" />
          </div>
        </div>
      </>
    )
  )
}

export default MateriVideo
